import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CalendarRange, CheckCircle2, Circle, Trophy, PlayCircle, Flame } from "lucide-react";
import { MOVES } from "../data/moves";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { youtubeEmbed } from "../lib/video";
import DrillCanvas from "./DrillCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOVE_BY_ID = Object.fromEntries(MOVES.map((m) => [m.id, m]));

const DrillCard = ({ move, done, saving, onToggle, index, isElite }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
    data-testid={`weekly-drill-${move.id}`}
    className={`rounded-xl border overflow-hidden bg-[#0f0f0f] transition-colors ${done ? "border-[#22C55E]/60" : isElite ? "border-[#D4AF37]/20" : "border-white/10"}`}>
    <div className="bg-[#0a0a0a] border-b border-white/10">
      <DrillCanvas path={move.path} cones={move.cones} label={move.drill?.name || move.name} />
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-subheadings font-bold uppercase tracking-wide text-sm leading-tight">{move.name}</div>
          <div className="text-[11px] text-white/45 mt-0.5">{move.drill?.name || "Technical drill"}</div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0" title={`Difficulty ${move.difficulty}/5`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`w-1.5 h-1.5 rounded-full ${n <= (move.difficulty || 1) ? (isElite ? "bg-[#D4AF37]" : "bg-[#C41E24]") : "bg-white/15"}`} />
          ))}
        </div>
      </div>
      <button
        data-testid={`weekly-drill-toggle-${move.id}`}
        onClick={() => onToggle(move.id, !done)}
        disabled={saving}
        className={`mt-3 w-full flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60 ${done ? "bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/50 hover:bg-[#22C55E]/25" : isElite ? "bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] text-black" : "bg-[#C41E24] hover:bg-[#A0161D] text-white"}`}>
        {done ? <><CheckCircle2 size={15} /> Done</> : <><Circle size={15} /> Mark Done</>}
      </button>
    </div>
  </motion.div>
);

export default function WeeklySession({ isElite = false }) {
  const { token } = usePlayerAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/weekly`, auth);
      setData(data);
    } catch (e) {
      setData({ drills: [] });
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const toggle = async (drillId, done) => {
    setSaving(drillId);
    try {
      const { data: res } = await axios.post(`${API}/weekly/complete`, { drill_id: drillId, done }, auth);
      setData((prev) => ({ ...prev, done: res.done, all_complete: res.all_complete, total_bonus: res.total_bonus, sessions_completed: res.sessions_completed, week_streak: res.week_streak }));
      if (res.just_awarded) {
        toast.success(`Weekly Session complete! +${res.bonus_points} bonus points earned 🎉`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that. Try again.");
    } finally {
      setSaving(null);
    }
  };

  if (!data || !data.drills || data.drills.length === 0) return null;

  const moves = data.drills.map((id) => MOVE_BY_ID[id]).filter(Boolean);
  const embed = youtubeEmbed(data.tip_video);
  const doneSet = new Set(data.done || []);
  const accent = isElite ? "#D4AF37" : "#C41E24";

  return (
    <section id="weekly-session" data-testid="weekly-session" className={`mb-10 rounded-2xl p-5 sm:p-6 ${isElite ? "glass-card border border-[#D4AF37]/20" : "border border-[#C41E24]/30 bg-gradient-to-b from-[#C41E24]/10 to-[#141414]"}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2.5">
          <CalendarRange size={18} style={{ color: accent }} />
          <h2 className="font-headings text-lg sm:text-xl uppercase tracking-wide">Weekly Training Session</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data.week_streak > 0 && (
            <span data-testid="weekly-streak" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#F5C518] bg-[#F5C518]/12 border border-[#F5C518]/40 px-3 py-1.5 rounded-full">
              <Flame size={13} /> {data.week_streak}-week streak
            </span>
          )}
          {data.sessions_completed > 0 && (
            <span data-testid="weekly-sessions-count" className="text-[11px] font-bold uppercase tracking-wider text-white/50">{data.sessions_completed} done</span>
          )}
          {data.all_complete ? (
            <span data-testid="weekly-bonus-earned" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/15 border border-[#22C55E]/40 px-3 py-1.5 rounded-full">
              <Trophy size={13} /> Bonus earned +{data.bonus_points}
            </span>
          ) : (
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Finish all 3 → +{data.bonus_points} pts</span>
          )}
        </div>
      </div>

      {embed && (
        <div data-testid="weekly-tip-video" className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle size={15} style={{ color: accent }} />
            <span className="text-xs font-bold uppercase tracking-widest text-white/70">{data.tip_title || "Coach's Tip of the Week"}</span>
          </div>
          <div className="relative w-full rounded-xl overflow-hidden border border-white/10" style={{ aspectRatio: "16 / 9" }}>
            <iframe title="Coach's Tip of the Week" src={embed} className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {moves.map((m, i) => (
          <DrillCard key={m.id} move={m} index={i} done={doneSet.has(m.id)} saving={saving === m.id} onToggle={toggle} isElite={isElite} />
        ))}
      </div>
    </section>
  );
}
